import { useTranslation } from 'react-i18next';
import DatePicker from '../../Fundamentals/DatePicker';
import Chip from '../../Fundamentals/Chip/Chip';
import helpers from '../../../Utils/helpers';

interface TransactionsFilterBarProps {
  searchText: string;
  onSearchChange: (value: string) => void;
  dateFrom: Date | null;
  dateTo: Date | null;
  onDateFromChange: (date: Date | null) => void;
  onDateToChange: (date: Date | null) => void;
  onClearDateFrom: () => void;
  onClearDateTo: () => void;
  dateResetKey: number;
  uninvoicedOnly: boolean;
  onToggleUninvoicedOnly: () => void;
  uninvoicedCount: number;
  onClearAll: () => void;
  shownCount: number;
  totalCount: number;
}

const TransactionsFilterBar = ({
  searchText,
  onSearchChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onClearDateFrom,
  onClearDateTo,
  dateResetKey,
  uninvoicedOnly,
  onToggleUninvoicedOnly,
  uninvoicedCount,
  onClearAll,
  shownCount,
  totalCount,
}: TransactionsFilterBarProps) => {
  const { t } = useTranslation();

  const hasActiveFilters =
    searchText !== '' || dateFrom !== null || dateTo !== null || uninvoicedOnly;

  return (
    <>
      <div className="row align-items-center mb-16">
        <div className="col-md-3">
          <input
            type="text"
            className="form-control"
            placeholder={t('customertransactions.searchPlaceholder') ?? ''}
            value={searchText}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>
        <div className="col-md-2">
          <DatePicker
            key={`from-${dateResetKey}`}
            value={dateFrom}
            showTime={false}
            placeholderText={t('customertransactions.dateFrom') ?? ''}
            onChange={(_value, dateObj) => onDateFromChange(dateObj)}
          />
        </div>
        <div className="col-md-2">
          <DatePicker
            key={`to-${dateResetKey}`}
            value={dateTo}
            showTime={false}
            placeholderText={t('customertransactions.dateTo') ?? ''}
            onChange={(_value, dateObj) => onDateToChange(dateObj)}
          />
        </div>
        <div className="col-md-auto">
          <button
            type="button"
            className={`btn btn-sm transactions-uninvoiced-toggle d-inline-flex align-items-center gap-8 text-nowrap ${uninvoicedOnly ? 'btn-primary' : 'btn-default'}`}
            onClick={onToggleUninvoicedOnly}
          >
            <span className="transactions-dot" />
            <span>{t('customertransactions.uninvoicedOnly')}</span>
            <span className="transactions-tab-count">{uninvoicedCount}</span>
          </button>
        </div>
        <div className="col text-right">
          <span className="text-dark-grey">
            {t('customertransactions.invoiceCount', { shown: shownCount, total: totalCount })}
          </span>
        </div>
      </div>
      {hasActiveFilters && (
        <div className="d-flex flex-wrap align-items-center gap-8 mb-16">
          {searchText !== '' && (
            <Chip
              label={`${t('customertransactions.searchLabel')}: ${searchText}`}
              onRemove={() => onSearchChange('')}
            />
          )}
          {dateFrom !== null && (
            <Chip
              label={`${t('customertransactions.dateFrom')}: ${helpers.formatDate(dateFrom)}`}
              onRemove={onClearDateFrom}
            />
          )}
          {dateTo !== null && (
            <Chip
              label={`${t('customertransactions.dateTo')}: ${helpers.formatDate(dateTo)}`}
              onRemove={onClearDateTo}
            />
          )}
          {uninvoicedOnly && (
            <Chip
              label={t('customertransactions.uninvoicedOnly')}
              onRemove={onToggleUninvoicedOnly}
            />
          )}
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onClearAll}>
            {t('customertransactions.clearAll')}
          </button>
        </div>
      )}
    </>
  );
};

export default TransactionsFilterBar;
